"use client";

import { useEffect } from "react";

/**
 * Bfcache restoration safety net.
 *
 * When the browser restores this page from the back-forward cache (Safari,
 * Chrome 105+, Firefox), DOM and React state are restored verbatim — but
 * `useEffect` lifecycles do NOT re-run. Modals that were open at navigation
 * time leave behind their backdrop, body `overflow:hidden`, and focus
 * listeners. The user lands on a "live" page where the visible header is
 * silently shadowed by an invisible-but-pointer-grabbing backdrop and
 * scroll is locked — buttons fail to respond, content can't scroll.
 *
 * On `pageshow` with `event.persisted === true` we:
 *   1. Restore `document.body.style.overflow` so scroll works.
 *   2. Remove orphaned `[data-pontimed-sheet]` elements from the DOM
 *      (backdrop + panel both carry this attribute via ResponsiveSheet).
 *   3. Dispatch `pontimed:bfcache-restored` so context providers
 *      (auth-modal, country, etc.) can reset their open/closed flags.
 *
 * Mount once at the app root next to HapticProvider.
 */
export function BfcacheGuard() {
  useEffect(() => {
    function onPageShow(event: PageTransitionEvent) {
      if (!event.persisted) return;

      // Body scroll: defensively clear regardless of who locked it.
      if (typeof document !== "undefined") {
        document.body.style.overflow = "";
      }

      // Remove orphaned sheet portal nodes from the bfcache snapshot.
      document.querySelectorAll("[data-pontimed-sheet]").forEach((el) => el.remove());

      // Let stateful providers reset.
      window.dispatchEvent(new CustomEvent("pontimed:bfcache-restored"));
    }

    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  return null;
}
